import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { addDaysIso, startOfWeekMonIso, todayLocalIso } from '../../shared/dates';
import { CaseManagerComplianceView } from './CaseManagerComplianceView';
import { DischargesView } from './DischargesView';
import { IntakesView } from './IntakesView';
import {
  ChipDropdown,
  SortTh,
  buildTextFilter,
  downloadCsv,
  useReportTable,
} from './_filters';

type Tab =
  | 'utilization'
  | 'noShow'
  | 'serviceDelivery'
  | 'caseManager'
  | 'discharges'
  | 'intakes';

export function ReportsPage() {
  const [tab, setTab] = useState<Tab>('caseManager');
  const today = todayLocalIso();
  const weekStart = startOfWeekMonIso(today);
  const [from, setFrom] = useState(addDaysIso(today, -30));
  const [to, setTo] = useState(today);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Reports</h1>
        <p className="text-sm text-slate-500">Provider load, no-show rate, service-delivery audit.</p>
      </header>

      <div className="flex flex-wrap gap-2">
        {([
          { v: 'caseManager', label: 'Case manager compliance' },
          { v: 'intakes', label: 'Intakes' },
          { v: 'discharges', label: 'Discharges' },
          { v: 'utilization', label: 'Provider utilization' },
          { v: 'noShow', label: 'No-show rate' },
          { v: 'serviceDelivery', label: 'Service delivery' },
        ] as { v: Tab; label: string }[]).map((t) => (
          <button
            key={t.v}
            className={tab === t.v ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setTab(t.v)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'caseManager' && <CaseManagerComplianceView />}
      {tab === 'intakes' && <IntakesView />}
      {tab === 'discharges' && <DischargesView />}
      {tab === 'utilization' && <ProviderUtilization weekStart={weekStart} />}
      {tab === 'noShow' && <NoShowRate from={from} to={to} setFrom={setFrom} setTo={setTo} />}
      {tab === 'serviceDelivery' && <ServiceDelivery from={from} to={to} setFrom={setFrom} setTo={setTo} />}
    </div>
  );
}

function ProviderUtilization({ weekStart }: { weekStart: string }) {
  const { data } = useQuery({
    queryKey: ['util', weekStart],
    queryFn: () => api.get('/reports/provider-utilization', { params: { weekStart } }).then((r) => r.data),
  });
  // Filters: resource type (PROVIDER / GROUP / ROOM / etc), provider-role,
  // minimum utilization %, free-text name search.
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [minUtil, setMinUtil] = useState<number>(0);
  const [q, setQ] = useState<string>('');

  const rows = (data ?? []) as any[];
  const ctrl = useReportTable(rows, {
    defaultSort: { key: 'utilizationPct', dir: 'desc' },
    filters: [
      typeFilter.length > 0 ? (r) => typeFilter.includes(r.resourceType) : undefined,
      roleFilter.length > 0
        ? (r) => roleFilter.includes(r.providerRole ?? r.resourceType ?? '')
        : undefined,
      minUtil > 0 ? (r) => (r.utilizationPct ?? 0) >= minUtil : undefined,
      buildTextFilter<any>(q, (r) => [r.resourceName, r.providerRole, r.resourceType]),
    ],
  });

  const allTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.resourceType))).sort(),
    [rows],
  );
  const allRoles = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.providerRole).filter((x): x is string => !!x)),
      ).sort(),
    [rows],
  );

  function exportCsv() {
    downloadCsv({
      filename: `provider-utilization-${weekStart}.csv`,
      headers: [
        'Resource',
        'Type',
        'Role',
        'Metric',
        'Weekly hrs',
        'Appt hrs',
        'Facilitator hrs',
        'Capacity (hrs)',
        'Utilization %',
        'Unique clients',
        'Seats taken',
        'Seats available',
      ],
      rows: ctrl.filtered,
      toRow: (u: any) => [
        u.resourceName,
        u.resourceType,
        u.providerRole ?? '',
        u.metricKind ?? '',
        u.capMinutes > 0 ? (u.weeklyMinutes / 60).toFixed(2) : '',
        u.apptMinutes != null ? (u.apptMinutes / 60).toFixed(2) : '',
        u.facilitatorMinutes != null ? (u.facilitatorMinutes / 60).toFixed(2) : '',
        u.capMinutes > 0 ? (u.capMinutes / 60).toFixed(2) : '',
        u.utilizationPct ?? '',
        u.uniqueClients ?? '',
        u.seatsTaken ?? '',
        u.seatsAvailable ?? '',
      ],
    });
  }

  if (!data) return null;
  return (
    <div className="space-y-3">
      <section className="card flex flex-wrap items-center gap-2 px-4 py-3">
        <input
          className="input h-8 w-56 text-xs"
          placeholder="Search name / role…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <ChipDropdown label="Type" options={allTypes as string[]} selected={typeFilter} onChange={setTypeFilter} />
        <ChipDropdown label="Role" options={allRoles as string[]} selected={roleFilter} onChange={setRoleFilter} />
        <label className="flex items-center gap-1 text-2xs text-ink-subtle">
          Min util %
          <input
            type="number"
            className="input h-7 w-14 px-1.5 text-xs"
            min={0}
            max={200}
            value={minUtil}
            onChange={(e) => setMinUtil(Number(e.target.value) || 0)}
          />
        </label>
        <button className="btn-primary h-8 px-3 text-xs" disabled={ctrl.filtered.length === 0} onClick={exportCsv}>
          Export CSV
        </button>
        <span className="ml-auto text-2xs text-ink-subtle">
          {ctrl.filtered.length} of {rows.length}
        </span>
      </section>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortTh col="resourceName" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('resourceName')}>
                Resource
              </SortTh>
              <SortTh col="providerRole" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('providerRole')}>
                Role / Type
              </SortTh>
              <SortTh col="weeklyMinutes" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('weeklyMinutes')}>
                Hours
              </SortTh>
              <SortTh col="capMinutes" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('capMinutes')}>
                Capacity
              </SortTh>
              <SortTh col="utilizationPct" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('utilizationPct')}>
                Util.
              </SortTh>
              <SortTh col="uniqueClients" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('uniqueClients')}>
                Clients
              </SortTh>
            </tr>
          </thead>
          <tbody>
            {ctrl.filtered.map((u: any) => (
              <tr key={u.resourceId} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{u.resourceName}</td>
                <td className="px-3 py-2">{u.providerRole ?? u.resourceType ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  {u.weeklyMinutes > 0 ? (u.weeklyMinutes / 60).toFixed(1) + 'h' : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {u.capMinutes > 0 ? (u.capMinutes / 60).toFixed(0) + 'h' : `cap ${u.capacity ?? '—'}`}
                </td>
                <td className="px-3 py-2 text-right">
                  <span
                    className={
                      u.utilizationPct > 100
                        ? 'pill-danger'
                        : u.utilizationPct > 90
                          ? 'pill-warn'
                          : u.metricKind === 'NONE'
                            ? 'pill-muted'
                            : 'pill-info'
                    }
                  >
                    {u.utilizationPct.toFixed(0)}%
                  </span>
                </td>
                <td className="px-3 py-2 text-right">{u.uniqueClients}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NoShowRate({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}) {
  const { data } = useQuery({
    queryKey: ['noshow', from, to],
    queryFn: () => api.get('/reports/no-show-rate', { params: { from, to } }).then((r) => r.data),
  });

  // Cohort rows shaped for sort/filter — convert from the object response.
  const cohortRows = useMemo(() => {
    const obj = data?.byCohort ?? {};
    return Object.entries(obj).map(([cohort, v]) => ({
      cohort,
      noShow: (v as any).noShow,
      total: (v as any).total,
      rate: (v as any).rate * 100,
    }));
  }, [data]);

  const [cohorts, setCohorts] = useState<string[]>([]);
  const [minRate, setMinRate] = useState<number>(0);

  const ctrl = useReportTable(cohortRows, {
    defaultSort: { key: 'rate', dir: 'desc' },
    filters: [
      cohorts.length > 0 ? (r) => cohorts.includes(r.cohort) : undefined,
      minRate > 0 ? (r) => r.rate >= minRate : undefined,
    ],
  });
  const cohortOptions = useMemo(() => cohortRows.map((r) => r.cohort).sort(), [cohortRows]);

  function exportCsv() {
    downloadCsv({
      filename: `no-show-rate-${from}_to_${to}.csv`,
      headers: ['Cohort', 'No-show', 'Total', 'Rate %'],
      rows: ctrl.filtered,
      toRow: (r: any) => [r.cohort, r.noShow, r.total, r.rate.toFixed(1)],
    });
  }

  return (
    <div className="space-y-3">
      <Range from={from} to={to} setFrom={setFrom} setTo={setTo} />
      {data && (
        <>
          <section className="card p-4 text-sm">
            <div className="text-2xl font-semibold">
              {(data.overall.rate * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-slate-500">
              {data.overall.noShow} no-shows / {data.overall.total} expected
            </p>
          </section>

          <section className="card flex flex-wrap items-center gap-2 px-4 py-3">
            <ChipDropdown
              label="Cohort"
              options={cohortOptions}
              selected={cohorts}
              onChange={setCohorts}
            />
            <label className="flex items-center gap-1 text-2xs text-ink-subtle">
              Min rate %
              <input
                type="number"
                className="input h-7 w-14 px-1.5 text-xs"
                min={0}
                max={100}
                value={minRate}
                onChange={(e) => setMinRate(Number(e.target.value) || 0)}
              />
            </label>
            <button
              className="btn-primary h-8 px-3 text-xs"
              disabled={ctrl.filtered.length === 0}
              onClick={exportCsv}
            >
              Export CSV
            </button>
            <span className="ml-auto text-2xs text-ink-subtle">
              {ctrl.filtered.length} of {cohortRows.length}
            </span>
          </section>

          <section className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <SortTh col="cohort" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('cohort')}>Cohort</SortTh>
                  <SortTh col="noShow" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('noShow')}>No-show</SortTh>
                  <SortTh col="total" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('total')}>Total</SortTh>
                  <SortTh col="rate" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('rate')}>Rate</SortTh>
                </tr>
              </thead>
              <tbody>
                {ctrl.filtered.map((r: any) => (
                  <tr key={r.cohort} className="border-t border-slate-100">
                    <td className="px-3 py-1">{r.cohort}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{r.noShow}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{r.total}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{r.rate.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

function ServiceDelivery({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}) {
  const { data } = useQuery({
    queryKey: ['serviceDelivery', from, to],
    queryFn: () => api.get('/reports/service-delivery', { params: { from, to } }).then((r) => r.data),
  });

  const [q, setQ] = useState('');
  const [cohorts, setCohorts] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [minDuration, setMinDuration] = useState<number>(0);

  const rows = (data?.rows ?? []) as any[];
  const ctrl = useReportTable<any>(rows, {
    defaultSort: { key: 'date', dir: 'desc' },
    filters: [
      cohorts.length > 0 ? (r) => cohorts.includes(r.cohortId?.[0] ?? '') : undefined,
      services.length > 0 ? (r) => services.includes(r.serviceType) : undefined,
      providers.length > 0 ? (r) => providers.includes(r.resourceName ?? '') : undefined,
      statuses.length > 0 ? (r) => statuses.includes(r.status) : undefined,
      minDuration > 0 ? (r) => (r.durationMin ?? 0) >= minDuration : undefined,
      buildTextFilter<any>(q, (r) => [r.clientName, r.cohortId, r.resourceName, r.serviceType]),
    ],
  });

  // Build dropdown options from data so we only show values that actually appear.
  const cohortOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.cohortId?.[0]).filter(Boolean))).sort(),
    [rows],
  );
  const serviceOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.serviceType))).sort(),
    [rows],
  );
  const providerOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.resourceName).filter(Boolean))).sort() as string[],
    [rows],
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.status))).sort(),
    [rows],
  );

  function exportCsv() {
    downloadCsv({
      filename: `service-delivery-${from}_to_${to}.csv`,
      headers: [
        'Date',
        'Cohort',
        'Client',
        'Service',
        'Kind',
        'Provider/Resource',
        'Place',
        'Start',
        'End',
        'Duration (min)',
        'Status',
        'Late minutes',
      ],
      rows: ctrl.filtered,
      toRow: (r: any) => [
        r.date,
        r.cohortId,
        r.clientName ?? '',
        r.serviceType,
        r.kind ?? '',
        r.resourceName ?? '',
        r.placeOfService ?? '',
        r.startMin,
        r.endMin,
        r.durationMin,
        r.status,
        r.lateMinutes ?? '',
      ],
    });
  }

  return (
    <div className="space-y-3">
      <Range from={from} to={to} setFrom={setFrom} setTo={setTo} />

      {data && (
        <section className="card flex flex-wrap items-center gap-2 px-4 py-3">
          <input
            className="input h-8 w-56 text-xs"
            placeholder="Search client / cohort / provider…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <ChipDropdown label="Cohort" options={cohortOptions as string[]} selected={cohorts} onChange={setCohorts} />
          <ChipDropdown label="Service" options={serviceOptions as string[]} selected={services} onChange={setServices} />
          <ChipDropdown label="Provider" options={providerOptions} selected={providers} onChange={setProviders} />
          <ChipDropdown label="Status" options={statusOptions as string[]} selected={statuses} onChange={setStatuses} />
          <label className="flex items-center gap-1 text-2xs text-ink-subtle">
            Min
            <input
              type="number"
              className="input h-7 w-14 px-1.5 text-xs"
              min={0}
              max={480}
              value={minDuration}
              onChange={(e) => setMinDuration(Number(e.target.value) || 0)}
            />
            min
          </label>
          <button
            className="btn-primary h-8 px-3 text-xs"
            disabled={ctrl.filtered.length === 0}
            onClick={exportCsv}
          >
            Export CSV
          </button>
          <span className="ml-auto text-2xs text-ink-subtle">
            {ctrl.filtered.length} of {rows.length}
          </span>
        </section>
      )}

      {data && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortTh col="date" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('date')}>Date</SortTh>
                <SortTh col="cohortId" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('cohortId')}>Cohort</SortTh>
                <SortTh col="clientName" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('clientName')}>Client</SortTh>
                <SortTh col="serviceType" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('serviceType')}>Service</SortTh>
                <SortTh col="resourceName" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('resourceName')}>Provider</SortTh>
                <SortTh col="durationMin" current={ctrl.sortBy} dir={ctrl.sortDir} align="right" onClick={() => ctrl.setSort('durationMin')}>Duration</SortTh>
                <SortTh col="status" current={ctrl.sortBy} dir={ctrl.sortDir} onClick={() => ctrl.setSort('status')}>Status</SortTh>
              </tr>
            </thead>
            <tbody>
              {ctrl.filtered.map((r: any, i: number) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1 font-mono text-2xs">{r.date}</td>
                  <td className="px-3 py-1 font-mono text-brand-700">{r.cohortId}</td>
                  <td className="px-3 py-1">{r.clientName}</td>
                  <td className="px-3 py-1">{r.serviceType}</td>
                  <td className="px-3 py-1 text-xs">{r.resourceName ?? r.providerName ?? ''}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{r.durationMin}</td>
                  <td className="px-3 py-1 text-xs">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Range({
  from,
  to,
  setFrom,
  setTo,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div>
        <label className="label">From</label>
        <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
      </div>
      <div>
        <label className="label">To</label>
        <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>
    </div>
  );
}
